import CopyText from "./copy_text_controller"
import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

export default class extends CopyText {
  static targets = [
    "radioButton", "configSection", "selectedType", "embedCode", "typeCard",
    "themeSettings", "buttonText", "buttonColor", "buttonColorText",
    "textColor", "textColorText", "buttonSize", "fontFamily", "fontBold",
    "previewContainer", "previewPlaceholder", "previewButton",
    "formSettings", "backgroundColor", "backgroundColorText",
    "primaryColor", "primaryColorText",
    "borderRadius", "formPreview",
    "formTextColor", "formTextColorText", "formFontFamily", "formButtonText",
    "showEventImage", "showEventDetails", "formButtonStyle",
    "cardSettings", "cardStyle", "cardBackgroundColor", "cardBackgroundColorText",
    "cardTextColor", "cardTextColorText", "cardAccentColor", "cardAccentColorText",
    "cardBorderRadius", "showCardImage", "showCardDescription", "showCardLocation",
    "showCardPrice", "cardButtonText"
  ]

  static values = {
    eventId: Number,
    eventSlug: String,
    copiedSuccess: String
  }

  connect() {
    // Widget configurator initialized
    // Initialize with registration button as default
    this.initializeDefaults()
  }

  // Get CSRF token for secure requests
  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : null
  }

  initializeDefaults() {
    // Set the current widget type
    this.currentWidgetType = 'registration-button'

    // Update selected type display
    this.selectedTypeTarget.innerHTML = '<p class="widget-type-tag">Widget Type: <span>Registration Button</span></p>'

    // Show theme settings and button preview
    this.themeSettingsTarget.style.display = 'block'
    this.showButtonPreview()

    // Update embed code with default settings
    this.updateEmbedCode('registration-button')
  }

  selectWidget(event) {
    const radio = event.currentTarget
    const widgetType = radio.value


    if (radio.checked) {
      // Store current widget type
      this.currentWidgetType = widgetType

      // Remove active class from all cards
      this.typeCardTargets.forEach(card => {
        card.classList.remove('active')
      })

      // Add active class to selected card
      radio.closest('.type-selection-card').classList.add('active')

      // Show configuration section
      this.configSectionTarget.style.display = 'block'

      // Update selected type display
      const typeNames = {
        'registration-button': 'Registration Button',
        'embedded-form': 'Embedded Registration Form',
        'event-card': 'Event Card'
      }

      this.selectedTypeTarget.innerHTML = `<p class="widget-type-tag">Widget Type: <span>${typeNames[widgetType]}</span></p>`

      // Show/hide theme settings based on widget type
      if (widgetType === 'registration-button') {
        this.themeSettingsTarget.style.display = 'block'
        if (this.hasFormSettingsTarget) this.formSettingsTarget.style.display = 'none'
        if (this.hasCardSettingsTarget) this.cardSettingsTarget.style.display = 'none'
        this.showButtonPreview()
      } else if (widgetType === 'embedded-form') {
        this.themeSettingsTarget.style.display = 'none'
        if (this.hasFormSettingsTarget) this.formSettingsTarget.style.display = 'block'
        if (this.hasCardSettingsTarget) this.cardSettingsTarget.style.display = 'none'
        this.showFormPreview()
      } else if (widgetType === 'event-card') {
        this.themeSettingsTarget.style.display = 'none'
        if (this.hasFormSettingsTarget) this.formSettingsTarget.style.display = 'none'
        if (this.hasCardSettingsTarget) this.cardSettingsTarget.style.display = 'block'
        this.showCardPreview()
      }

      // Update embed code
      this.updateEmbedCode(widgetType)
    }
  }

  showButtonPreview() {
    this.previewPlaceholderTarget.style.display = 'none'
    this.previewButtonTarget.style.display = 'inline-block'
    if (this.hasFormPreviewTarget) {
      this.formPreviewTarget.style.display = 'none'
    }
    this.updatePreview()
  }

  hideButtonPreview() {
    this.previewPlaceholderTarget.style.display = 'block'
    this.previewButtonTarget.style.display = 'none'
    if (this.hasFormPreviewTarget) this.formPreviewTarget.style.display = 'none'
  }

  showFormPreview() {
    if (this.hasFormPreviewTarget) {
      this.previewPlaceholderTarget.style.display = 'none'
      this.previewButtonTarget.style.display = 'none'
      this.formPreviewTarget.style.display = 'block'
      this.updateFormPreview()
    } else {
      console.error('formPreviewTarget not found')
    }
  }

  updatePreview() {
    if (!this.hasPreviewButtonTarget) return

    const button = this.previewButtonTarget
    const text = this.buttonTextTarget.value
    const bgColor = this.buttonColorTarget.value
    const textColor = this.textColorTarget.value
    const size = this.buttonSizeTarget.value
    const fontFamily = this.fontFamilyTarget.value
    const isBold = this.fontBoldTarget.checked
    const cornerStyleInput = document.querySelector('input[name="cornerStyle"]:checked')
    const cornerStyle = cornerStyleInput ? cornerStyleInput.value : 'rounded'

    // Sync color text inputs
    this.buttonColorTextTarget.value = bgColor
    this.textColorTextTarget.value = textColor

    // Update button text
    button.textContent = text

    // Update colors
    button.style.backgroundColor = bgColor
    button.style.color = textColor
    button.style.borderColor = bgColor

    // Update font family
    const fontMap = {
      'default': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      'arial': 'Arial, sans-serif',
      'helvetica': '"Helvetica Neue", Helvetica, Arial, sans-serif',
      'georgia': 'Georgia, serif',
      'times': '"Times New Roman", Times, serif',
      'courier': '"Courier New", Courier, monospace',
      'verdana': 'Verdana, Geneva, sans-serif',
      'tahoma': 'Tahoma, Geneva, sans-serif',
      'trebuchet': '"Trebuchet MS", Helvetica, sans-serif',
      'opensans': '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
      'comic': '"Comic Sans MS", cursive, sans-serif'
    }
    button.style.fontFamily = fontMap[fontFamily] || fontMap['default']

    // Apply font weight based on bold option
    button.style.letterSpacing = 'normal'
    button.style.fontWeight = isBold ? 'bold' : 'normal'

    // Update size classes
    button.className = 'btn'
    button.style.border = '1px solid'
    button.style.transition = 'all 0.2s ease'
    button.style.cursor = 'pointer'

    switch(size) {
      case 'small':
        button.classList.add('btn-sm')
        button.style.padding = '0.25rem 0.5rem'
        button.style.fontSize = '0.875rem'
        break
      case 'large':
        button.classList.add('btn-lg')
        button.style.padding = '0.5rem 1rem'
        button.style.fontSize = '1.25rem'
        break
      case 'xl':
        button.style.padding = '0.75rem 1.5rem'
        button.style.fontSize = '1.5rem'
        break
      case 'xxl':
        button.style.padding = '1rem 2rem'
        button.style.fontSize = '1.75rem'
        break
      default: // medium
        button.style.padding = '0.375rem 0.75rem'
        button.style.fontSize = '1rem'
    }

    // Update corner style - make the difference more noticeable
    if (cornerStyle === 'square') {
      button.style.borderRadius = '0px'
    } else {
      button.style.borderRadius = '8px'
    }

    // Update embed code with new settings
    if (this.currentWidgetType === 'registration-button') {
      this.updateEmbedCode(this.currentWidgetType)
    }
  }

  updateFormPreview() {
    if (!this.hasFormPreviewTarget) return

    const backgroundColor = this.hasBackgroundColorTarget ? this.backgroundColorTarget.value : '#ffffff'
    const textColor = this.hasFormTextColorTarget ? this.formTextColorTarget.value : '#333333'
    const primaryColor = this.hasPrimaryColorTarget ? this.primaryColorTarget.value : '#007bff'
    const borderRadius = this.hasBorderRadiusTarget ? this.borderRadiusTarget.value : '8'
    const fontFamily = this.hasFormFontFamilyTarget ? this.formFontFamilyTarget.value : 'default'
    const buttonText = this.hasFormButtonTextTarget ? this.formButtonTextTarget.value : 'Register Now'
    const showEventImage = this.hasShowEventImageTarget ? this.showEventImageTarget.checked : true
    const showEventDetails = this.hasShowEventDetailsTarget ? this.showEventDetailsTarget.checked : true
    const buttonStyle = this.hasFormButtonStyleTarget ?
      document.querySelector('input[name="formButtonStyle"]:checked')?.value || 'rounded' : 'rounded'


    // Make AJAX request to get form preview
    const params = new URLSearchParams({
      widget_type: 'registration-form',
      background_color: backgroundColor,
      text_color: textColor,
      primary_color: primaryColor,
      border_radius: borderRadius,
      font_family: fontFamily,
      button_text: buttonText,
      show_event_image: showEventImage,
      show_event_details: showEventDetails,
      button_style: buttonStyle
    })

    const url = `${window.location.pathname}/preview?${params}`

    get(url, { responseKind: 'json' })
    .then(async response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json
      if (data.status === 'success') {
        this.formPreviewTarget.innerHTML = data.html
        // Ensure the form preview is visible
        this.formPreviewTarget.style.display = 'block'
        // Attach event listeners for description toggles and quantity controls
        this.attachDescriptionToggles()
        this.attachQuantityControls()
      } else {
        // Keep form preview visible but show error
        this.formPreviewTarget.innerHTML = '<div class="alert alert-warning">Unable to load preview</div>'
        this.formPreviewTarget.style.display = 'block'
      }
    })
    .catch(error => {
      // Keep form preview visible but show error message
      this.formPreviewTarget.innerHTML = '<div class="alert alert-danger">Error loading preview</div>'
      this.formPreviewTarget.style.display = 'block'
    })

    // Update embed code with new settings
    if (this.currentWidgetType === 'embedded-form') {
      this.updateFormEmbedCode()
    }
  }

  updateFormEmbedCode() {
    const backgroundColor = this.hasBackgroundColorTarget ? this.backgroundColorTarget.value : '#ffffff'
    const textColor = this.hasFormTextColorTarget ? this.formTextColorTarget.value : '#333333'
    const primaryColor = this.hasPrimaryColorTarget ? this.primaryColorTarget.value : '#007bff'
    const borderRadius = this.hasBorderRadiusTarget ? this.borderRadiusTarget.value : '8'
    const fontFamily = this.hasFormFontFamilyTarget ? this.formFontFamilyTarget.value : 'default'
    const buttonText = this.hasFormButtonTextTarget ? this.formButtonTextTarget.value : 'Register Now'
    const showEventImage = this.hasShowEventImageTarget ? this.showEventImageTarget.checked : true
    const showEventDetails = this.hasShowEventDetailsTarget ? this.showEventDetailsTarget.checked : true
    const buttonStyle = this.hasFormButtonStyleTarget ?
      document.querySelector('input[name="formButtonStyle"]:checked')?.value || 'rounded' : 'rounded'

    // Make AJAX request to get embed code
    const params = new URLSearchParams({
      widget_type: 'registration-form',
      background_color: backgroundColor,
      text_color: textColor,
      primary_color: primaryColor,
      border_radius: borderRadius,
      font_family: fontFamily,
      button_text: buttonText,
      show_event_image: showEventImage,
      show_event_details: showEventDetails,
      button_style: buttonStyle
    })

    get(`${window.location.pathname}/embed_code?${params}`, { responseKind: 'json' })
    .then(async response => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const data = await response.json
      if (data.status === 'success') {
        this.embedCodeTarget.textContent = data.embed_code
      }
    })
    .catch(error => {
      console.error('Error updating embed code:', error)
      if (this.hasEmbedCodeTarget) {
        this.embedCodeTarget.textContent = 'Could not load embed code.'
      }
    })
  }

  updateColorFromText(event) {
    const textValue = this.buttonColorTextTarget.value
    if (this.isValidHex(textValue)) {
      this.buttonColorTarget.value = textValue
      this.updatePreview()
    }
  }

  updateTextColorFromText(event) {
    const textValue = this.textColorTextTarget.value
    if (this.isValidHex(textValue)) {
      this.textColorTarget.value = textValue
      this.updatePreview()
    }
  }

  updateFormTextColorFromText(event) {
    const textValue = this.formTextColorTextTarget.value
    if (this.isValidHex(textValue)) {
      this.formTextColorTarget.value = textValue
      this.updateFormPreview()
    }
  }

  updateBackgroundColorFromText(event) {
    const textValue = this.backgroundColorTextTarget.value
    if (this.isValidHex(textValue)) {
      this.backgroundColorTarget.value = textValue
      this.updateFormPreview()
    }
  }

  updatePrimaryColorFromText(event) {
    const textValue = this.primaryColorTextTarget.value
    if (this.isValidHex(textValue)) {
      this.primaryColorTarget.value = textValue
      this.updateFormPreview()
    }
  }

  isValidHex(hex) {
    return /^#([0-9A-F]{3}){1,2}$/i.test(hex)
  }

  selectCard(event) {
    // If clicking on the card but not the radio button, select the radio
    if (event.target.type !== 'radio') {
      const radio = event.currentTarget.querySelector('input[type="radio"]')
      if (radio) {
        radio.checked = true
        // Trigger the change event
        this.selectWidget({ currentTarget: radio })
      }
    }
  }

  updateEmbedCode(type) {
    const eventId = this.eventIdValue
    const eventSlug = this.eventSlugValue || ''
    const origin = window.location.origin

    let code = ''

    if (type === 'registration-button') {
      // Get theme settings for button
      const text = this.buttonTextTarget.value
      const bgColor = this.buttonColorTarget.value
      const textColor = this.textColorTarget.value
      const size = this.buttonSizeTarget.value
      const fontFamily = this.fontFamilyTarget.value
      const isBold = this.fontBoldTarget.checked
      const cornerStyle = document.querySelector('input[name="cornerStyle"]:checked')?.value || 'rounded'

      code = `<!-- Registration Button Widget -->
<script src="${origin}/widgets/button.js"></script>
<div data-widget="registration-button"
     data-event-id="${eventId}"
     data-button-text="${this.escapeHtml(text)}"
     data-button-color="${bgColor}"
     data-text-color="${textColor}"
     data-button-size="${size}"
     data-font-family="${fontFamily}"
     data-font-bold="${isBold}"
     data-corner-style="${cornerStyle}">
</div>`
    } else if (type === 'embedded-form') {
      // Embedded form and event card: fetch iframe embed code from server (same as promote flow)
      this.updateFormEmbedCode()
      return
    } else if (type === 'event-card') {
      // Card will use iframe, update through AJAX
      this.updateCardEmbedCode()
      return // Exit early since we're handling via AJAX
    }

    this.embedCodeTarget.textContent = code
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  copyEmbedCode(event) {
    const code = this.embedCodeTarget.textContent
    if (this.copyToClipboard(code)) {
      if (typeof this.copiedSuccessValue !== 'undefined' && this.copiedSuccessValue) {
        if (typeof displayFlashMessage === 'function') {
          displayFlashMessage('success', this.copiedSuccessValue)
        }
      } else {
        const button = event.currentTarget
        const originalText = button.textContent
        button.textContent = 'Copied!'
        button.classList.add('btn-success')
        button.classList.remove('btn-primary')
        setTimeout(() => {
          button.textContent = originalText
          button.classList.remove('btn-success')
          button.classList.add('btn-primary')
        }, 2000)
      }
    }
  }

  attachDescriptionToggles() {
    // Find all toggle buttons in the preview
    const toggleButtons = this.formPreviewTarget.querySelectorAll('.widget-toggle-btn')

    toggleButtons.forEach(button => {
      // Remove any existing onclick and add new event listener
      button.onclick = null
      button.addEventListener('click', (e) => {
        e.preventDefault()
        // Find the wrapper and its content/arrow elements
        const wrapper = button.closest('.widget-read-more-wrapper')
        const content = wrapper.querySelector('.widget-read-more-content')
        const arrow = wrapper.querySelector('.widget-arrow')

        if (content && arrow) {
          if (content.style.display === 'none' || content.style.display === '') {
            content.style.display = 'block'
            arrow.style.transform = 'rotate(180deg)'
          } else {
            content.style.display = 'none'
            arrow.style.transform = 'rotate(0deg)'
          }
        }
      })
    })
  }

  attachQuantityControls() {
    if (!this.formPreviewTarget) return

    // Find all quantity controls in the preview
    const quantityControls = this.formPreviewTarget.querySelectorAll('.widget-quantity-controls')

    quantityControls.forEach(control => {
      const minusBtn = control.querySelector('.widget-btn-minus')
      const plusBtn = control.querySelector('.widget-btn-plus')
      const display = control.querySelector('.widget-quantity-display')
      const hiddenInput = control.parentElement.querySelector('input[type="hidden"]')

      if (minusBtn && plusBtn && display && hiddenInput) {
        // Remove existing event listeners
        minusBtn.onclick = null
        plusBtn.onclick = null

        // Add minus button functionality
        minusBtn.addEventListener('click', (e) => {
          e.preventDefault()
          const currentValue = parseInt(display.textContent) || 0
          if (currentValue > 0) {
            const newValue = currentValue - 1
            display.textContent = newValue
            hiddenInput.value = newValue
            this.updateTotalDisplay()
          }
        })

        // Add plus button functionality
        plusBtn.addEventListener('click', (e) => {
          e.preventDefault()
          const currentValue = parseInt(display.textContent) || 0
          const newValue = currentValue + 1
          display.textContent = newValue
          hiddenInput.value = newValue
          this.updateTotalDisplay()
        })
      }
    })
  }

  updateTotalDisplay() {
    if (!this.formPreviewTarget) return

    const totalSection = this.formPreviewTarget.querySelector('.total-section')
    const totalAmount = this.formPreviewTarget.querySelector('.total-amount')

    if (!totalSection || !totalAmount) return

    let total = 0
    let hasTickets = false

    // Calculate total from all ticket quantities
    const ticketWrappers = this.formPreviewTarget.querySelectorAll('.widget-bundle-wrapper')
    ticketWrappers.forEach(wrapper => {
      const quantityDisplay = wrapper.querySelector('.widget-quantity-display')
      const priceElement = wrapper.querySelector('.widget-price')
      const hiddenInput = wrapper.querySelector('input[type="hidden"]')

      if (quantityDisplay && priceElement && hiddenInput) {
        const quantity = parseInt(quantityDisplay.textContent) || 0
        if (quantity > 0) {
          hasTickets = true
          // Extract price from text (handle both "$10.00" and "Free")
          const priceText = priceElement.textContent.trim()
          if (priceText !== 'Free') {
            const price = parseFloat(priceText.replace('$', '')) || 0
            total += price * quantity
          }
        }
      }
    })

    // Show/hide total section and update submit button
    if (hasTickets) {
      totalSection.style.display = 'block'
      totalAmount.textContent = total > 0 ? `$${total.toFixed(2)}` : 'Free'
      // Enable submit button
      const submitBtn = this.formPreviewTarget.querySelector('button[type="submit"]')
      if (submitBtn) {
        submitBtn.disabled = false
        submitBtn.style.opacity = '1'
        submitBtn.style.cursor = 'pointer'
      }
    } else {
      totalSection.style.display = 'none'
      // Disable submit button
      const submitBtn = this.formPreviewTarget.querySelector('button[type="submit"]')
      if (submitBtn) {
        submitBtn.disabled = true
        submitBtn.style.opacity = '0.6'
        submitBtn.style.cursor = 'not-allowed'
      }
    }
  }

  // Event Card Widget Methods
  showCardPreview() {
    this.previewPlaceholderTarget.style.display = 'none'
    this.previewButtonTarget.style.display = 'none'
    if (this.hasFormPreviewTarget) {
      this.formPreviewTarget.innerHTML = ''
      this.formPreviewTarget.style.display = 'block'
    }
    this.updateCardPreview()
  }

  updateCardPreview() {
    const params = this.getCardParams()

    // Build the correct URL using the current path with query params
    const queryParams = new URLSearchParams({ ...params, widget_type: 'event-card' })
    const url = `${window.location.pathname}/preview?${queryParams}`

    get(url, { responseKind: 'json' })
    .then(async response => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const data = await response.json
      if (data.status === 'success') {
        if (this.hasFormPreviewTarget) {
          this.formPreviewTarget.innerHTML = data.html
          this.formPreviewTarget.style.display = 'block'
        }
      }
    })
    .catch(error => {
      console.error('Error updating card preview:', error)
      if (this.hasFormPreviewTarget) {
        this.formPreviewTarget.innerHTML = '<div class="alert alert-danger">Error loading preview</div>'
        this.formPreviewTarget.style.display = 'block'
      }
    })

    // Update embed code for the card
    if (this.currentWidgetType === 'event-card') {
      this.updateCardEmbedCode()
    }
  }

  getCardParams() {
    return {
      card_style: this.hasCardStyleTarget ? this.cardStyleTarget.value : 'compact',
      card_background_color: this.hasCardBackgroundColorTarget ? this.cardBackgroundColorTarget.value : '#ffffff',
      card_text_color: this.hasCardTextColorTarget ? this.cardTextColorTarget.value : '#333333',
      card_accent_color: this.hasCardAccentColorTarget ? this.cardAccentColorTarget.value : '#007bff',
      card_border_radius: this.hasCardBorderRadiusTarget ? this.cardBorderRadiusTarget.value : '12',
      show_card_image: this.hasShowCardImageTarget ? this.showCardImageTarget.checked : true,
      show_card_description: this.hasShowCardDescriptionTarget ? this.showCardDescriptionTarget.checked : true,
      show_card_location: this.hasShowCardLocationTarget ? this.showCardLocationTarget.checked : true,
      show_card_price: this.hasShowCardPriceTarget ? this.showCardPriceTarget.checked : true,
      card_button_text: this.hasCardButtonTextTarget ? this.cardButtonTextTarget.value : 'Get Tickets'
    }
  }

  updateCardEmbedCode() {
    const params = this.getCardParams()

    // Build the correct URL using the current path with query params
    const queryParams = new URLSearchParams({ ...params, widget_type: 'event-card' })
    const url = `${window.location.pathname}/embed_code?${queryParams}`

    get(url, { responseKind: 'json' })
    .then(async response => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const data = await response.json
      if (data.status === 'success') {
        this.embedCodeTarget.textContent = data.embed_code
      }
    })
    .catch(error => {
      console.error('Error updating card embed code:', error)
      if (this.hasEmbedCodeTarget) {
        this.embedCodeTarget.textContent = 'Could not load embed code.'
      }
    })
  }

  // Color sync methods for card widget
  updateCardBackgroundColorFromText(event) {
    const color = event.currentTarget.value
    if (this.hasCardBackgroundColorTarget && /^#[0-9A-F]{6}$/i.test(color)) {
      this.cardBackgroundColorTarget.value = color
      this.updateCardPreview()
    }
  }

  updateCardTextColorFromText(event) {
    const color = event.currentTarget.value
    if (this.hasCardTextColorTarget && /^#[0-9A-F]{6}$/i.test(color)) {
      this.cardTextColorTarget.value = color
      this.updateCardPreview()
    }
  }

  updateCardAccentColorFromText(event) {
    const color = event.currentTarget.value
    if (this.hasCardAccentColorTarget && /^#[0-9A-F]{6}$/i.test(color)) {
      this.cardAccentColorTarget.value = color
      this.updateCardPreview()
    }
  }
}


